import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AddUnitOperationLinksModal from 'studies/typed/shared/flowcharts/unit-op-linking/AddUnitOperationLinksModal';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {type ActionItem} from 'ui-library/headers/Header';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  LinkToExistingUnitOperationsButtonQuery,
  LinkToExistingUnitOperationsButtonQueryVariables,
} from './__generated/LinkToExistingUnitOperationsButtonQuery';

export const LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY = gql`
  query LinkToExistingUnitOperationsButtonQuery($studyId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type StudyNode = ExtractByTypename<LinkToExistingUnitOperationsButtonQuery['study'], 'StudyNode'>;

export interface LinkToExistingUnitOperationsActionParams {
  studyId: string;
  procedureRunId: string;
  procedureRunName: string;
  studyType: StudyType;
}

/**
 * Builds the "Link to existing" header action for a procedure run. Runs its own query to decide whether
 * there are other runs to link to. Hook can be consumed by the procedure run header
 * action or the LinkToExistingUnitOperationsButton component.
 */
export function useLinkToExistingUnitOperationsAction(
  props: LinkToExistingUnitOperationsActionParams
): ActionItem {
  const studyId = props.studyId;
  const result = useBenchlingQuery<
    LinkToExistingUnitOperationsButtonQuery,
    LinkToExistingUnitOperationsButtonQueryVariables
  >(LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY, {
    variables: {studyId},
    renderCachedDataWhenRefetching: true,
  });

  let isDisabled = true;
  let disabledTooltip: string | undefined;

  if (result.type === QueryResultType.PENDING) {
    disabledTooltip = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const study: StudyNode = result.data.study;
    isDisabled = study.procedureRunCount === 1;
    disabledTooltip = `No existing ${getProcedureRunUIStringForStudyType(props.studyType, {plural: true})} to link to`;
  }
  assert(props.procedureRunId != null, 'Procedure Run is not selected');
  assert(props.procedureRunName != null, 'Procedure Run is not selected');

  const onSelect = async (): Promise<void> => {
    await legacyRunModal(AddUnitOperationLinksModal, {
      studyId: props.studyId,
      procedureRunId: props.procedureRunId,
      procedureRunName: props.procedureRunName,
    });
  };

  return {
    icon: 'diagram-nested',
    name: 'Link to existing',
    onSelect,
    isDisabled,
    disabledTooltip,
    dataTestAttrs: {component: 'LinkToExistingButton', element: 'button'},
  };
}
